// src/components/DateNotificationForm.js
import React, { useState } from 'react';
import { db } from '../services/firebase';
import { collection, addDoc, Timestamp, query, where, getDocs } from 'firebase/firestore';
import { Check, X } from 'lucide-react';

/**
 * Formulaire permettant aux clients de s'inscrire pour être notifiés
 * lorsqu'une date non encore ouverte devient disponible
 * Style noir et blanc sobre, cohérent avec le bloc supérieur
 */
const DateNotificationForm = ({ language = 'fr', customTitle, customText, maxPublicBookableDate }) => {
  const [email, setEmail] = useState('');
  const [requestedDate, setRequestedDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showAlreadyOpenModal, setShowAlreadyOpenModal] = useState(false);

  const translations = {
    fr: {
      title: customTitle || 'La date souhaitée n\'est pas encore ouverte ?',
      text: customText || 'La période que vous souhaitez n\'est pas encore ouverte à la réservation.\nIndiquez votre email et la date d\'arrivée : nous vous préviendrons dès l\'ouverture.',
      emailPlaceholder: 'votre@email.com',
      datePlaceholder: 'Date d\'arrivée',
      button: 'Me prévenir',
      buttonLoading: 'Envoi...',
      successTitle: 'Inscription confirmée',
      successMessage: 'Merci ! Nous vous préviendrons dès l\'ouverture de cette date.',
      errorEmail: 'Veuillez entrer un email valide',
      errorDate: 'Veuillez sélectionner une date',
      errorAlready: 'Vous êtes déjà inscrit pour cette date',
      errorGeneral: 'Une erreur est survenue. Veuillez réessayer.',
      alreadyOpenTitle: 'Date déjà ouverte',
      alreadyOpenMessage: 'Cette date est déjà ouverte ! Si elle est en rouge sur le calendrier, c\'est que nous sommes déjà complet, sinon réservez dès maintenant.',
      alreadyOpenButton: 'OK'
    },
    en: {
      title: customTitle || 'Desired date not yet available?',
      text: customText || 'The period you wish to book is not yet open for reservations.\nProvide your email and arrival date: we will notify you as soon as it opens.',
      emailPlaceholder: 'your@email.com',
      datePlaceholder: 'Arrival date',
      button: 'Notify me',
      buttonLoading: 'Sending...',
      successTitle: 'Registration confirmed',
      successMessage: 'Thank you! We will notify you when this date opens.',
      errorEmail: 'Please enter a valid email',
      errorDate: 'Please select a date',
      errorAlready: 'You are already registered for this date',
      errorGeneral: 'An error occurred. Please try again.',
      alreadyOpenTitle: 'Date already open',
      alreadyOpenMessage: 'This date is already open! If it\'s red on the calendar, it means we\'re fully booked, otherwise book now.',
      alreadyOpenButton: 'OK'
    }
  };

  const t = translations[language] || translations.fr;

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!validateEmail(email)) {
      setError(t.errorEmail);
      return;
    }

    if (!requestedDate) {
      setError(t.errorDate);
      return;
    }

    setLoading(true);

    try {
      // Vérifier si la date est déjà ouverte
      if (maxPublicBookableDate && requestedDate <= maxPublicBookableDate) {
        setShowAlreadyOpenModal(true);
        setLoading(false);
        return;
      }

      // Vérifier si la demande existe déjà
      const existingQuery = query(
        collection(db, 'booking_notifications'),
        where('email', '==', email.toLowerCase().trim()),
        where('requestedDate', '==', requestedDate),
        where('notified', '==', false)
      );
      
      const existingDocs = await getDocs(existingQuery);
      
      if (!existingDocs.empty) {
        setError(t.errorAlready);
        setLoading(false);
        return;
      }

      // Créer la demande de notification
      await addDoc(collection(db, 'booking_notifications'), {
        email: email.toLowerCase().trim(),
        requestedDate: requestedDate,
        language: language,
        createdAt: Timestamp.now(),
        notified: false
      });

      setSuccess(true);
      setEmail('');
      setRequestedDate('');

      // Cacher le message de succès après 5 secondes
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      console.error('Error creating notification request:', err);
      setError(t.errorGeneral);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-6xl mx-auto px-2 sm:px-8 py-6">
        <div className="bg-white border-2 border-black rounded-lg p-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Check className="w-5 h-5" />
            <h3 className="text-lg font-semibold">{t.successTitle}</h3>
          </div>
          <p className="text-sm text-gray-800">
            {t.successMessage}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-2 sm:px-8 py-6">
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <h3 className="text-base font-semibold text-gray-900 mb-2">
          {t.title}
        </h3>
        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap mb-4">
          {t.text}
        </p>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.emailPlaceholder}
              className="flex-1 max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-black text-sm"
              disabled={loading}
            />
            <input
              type="date"
              value={requestedDate}
              onChange={(e) => setRequestedDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-black text-sm"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap text-sm"
            >
              {loading ? t.buttonLoading : t.button}
            </button>
          </div>

          {error && (
            <div className="flex items-center justify-center gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded p-2 max-w-md mx-auto">
              <X className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </form>
      </div>

      {/* Modal : Date déjà ouverte */}
      {showAlreadyOpenModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full border-2 border-black">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                {t.alreadyOpenTitle}
              </h3>
              <p className="text-gray-700 text-center mb-6 leading-relaxed">
                {t.alreadyOpenMessage}
              </p>
              <div className="flex justify-center">
                <button
                  onClick={() => {
                    setShowAlreadyOpenModal(false);
                    setEmail('');
                    setRequestedDate('');
                  }}
                  className="px-8 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  {t.alreadyOpenButton}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateNotificationForm;